import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import Tooltip from "@mui/material/Tooltip";
import { motion } from "framer-motion";
import CachedIcon from "@mui/icons-material/Cached";
import FilterImg from "../../assets/Images/Funnel.svg";
import RcsDashboardFilterModal from "./RcsDashboardFilterModal";

ChartJS.register(ArcElement, ChartTooltip, Legend);

const formatDateForInput = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${year}-${month}-${day}`; // ✅ for <input type="date">
};

const formatDateForBackend = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}-${month}-${year}`; // ✅ for API
};

// Start of current month
const getStartOfMonth = () => {
  const today = new Date();
  return formatDateForInput(new Date(today.getFullYear(), today.getMonth(), 1));
};

// Today
const getTodayDate = () => {
  return formatDateForInput(new Date());
};

// Helper for semi-circle progress bar
const SemiCircleChart = ({ value, color }) => {
  const data = {
    datasets: [
      {
        data: [value, 100 - value],
        backgroundColor: [color, "#EAEAEA"],
        borderWidth: 0,
      },
    ],
  };

  const options = {
    rotation: -90, // start angle
    circumference: 180, // semi-circle
    cutout: "70%", // inner radius
    plugins: {
      tooltip: { enabled: false },
      legend: { display: false },
    },
  };

  return (
    <div className="relative flex items-center justify-center w-[280px] h-[280px]">
      <Doughnut data={data} options={options} />
      <div
        className={`absolute inset-0 flex items-center justify-center text-[26px] font-bold !mt-[110px] text-[${color}]`}
      >
        {value?.toFixed(2)}%
      </div>
    </div>
  );
};

const defaultFilterData = {
  campaignId: "",
  fromDate: getTodayDate(),
  toDate: getTodayDate(),
  companyName: "",
};

const CampaignAnalytics = ({ rcsDashboardData, callApi, loading }) => {
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [fromDateError, setFromDateError] = useState("");
  const [toDateError, setToDateError] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [noData, setNoData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDetails, setPageDetails] = useState(null);
  const [RCSHistoryData, setRCSHistoryData] = useState([]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    if (!fromDateError && !toDateError) {
      // Format dates for backend
      const formattedFromDate = formatDateForBackend(fromDate);
      const formattedToDate = formatDateForBackend(toDate);

      callApi({
        fromDate:
          formatDateForBackend(filterData?.fromDate) || formattedFromDate,
        toDate: formatDateForBackend(filterData?.toDate) || formattedToDate,
        campaignId: filterData?.campaignId || "",
      });
    }
  }, [fromDate, toDate, callApi]);

  const getPercentage = (count, total) => {
    if (!total || total === 0) return "0%";
    return ((count / total) * 100).toFixed(2) + "%";
  };

  const formatNumberWithCommas = (number) => {
    if (!number && number !== 0) return "";
    const num = parseFloat(number.toString().replace(/,/g, ""));
    if (isNaN(num)) return "0";

    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleFromDateChange = (e) => {
    const newFromDate = e.target.value;
    setFromDate(newFromDate);

    if (toDate && new Date(newFromDate) > new Date(toDate)) {
      setFromDateError("From Date must be before To Date");
    } else {
      setFromDateError("");
      setToDateError(""); // clear if valid
    }
  };

  const handleToDateChange = (e) => {
    const newToDate = e.target.value;
    setToDate(newToDate);

    if (fromDate && new Date(newToDate) < new Date(fromDate)) {
      setToDateError("To Date must be after From Date");
    } else {
      setToDateError("");
      setFromDateError(""); // clear if valid
    }
  };

  const tooltipContent = (
    <div className="text-sm text-gray-800 w-[200px]">
      <div className="flex justify-between">
        <span>Delivered</span>
        <span>{rcsDashboardData?.deliveredCount || 0}</span>
      </div>
      <div className="flex justify-between">
        <span>Read</span>
        <span>{rcsDashboardData?.readCount || 0}</span>
      </div>
      <div className="border-t my-1 border-dashed border-gray-300"></div>
      <div className="flex justify-between">
        <span>Un Read</span>
        <span>{rcsDashboardData?.unreadCount || 0}</span>
      </div>
    </div>
  );

  const readTooltipContent = (
    <div className="text-sm text-gray-800 w-[200px]">
      <div className="flex justify-between">
        <span>Read</span>
        <span>{rcsDashboardData?.readCount || 0}</span>
      </div>
      <div className="flex justify-between">
        <span>Click</span>
        <span>{rcsDashboardData?.clickCount || 0}</span>
      </div>
      <div className="border-t my-1 border-dashed border-gray-300"></div>
      <div className="flex justify-between">
        <span>Un Click</span>
        <span>{rcsDashboardData?.unclickedCount || 0}</span>
      </div>
    </div>
  );

  const failedTooltipContent = (
    <div className="w-[350px] max-h-60 overflow-y-auto custom-scrollbar">
      <div>
        {Object.entries(rcsDashboardData?.failedDetails || {}).map(
          ([key, value], index) => (
            <div
              key={index}
              className="grid grid-cols-2 gap-2 py-1 rounded px-1 transition-all duration-150"
            >
              <span className="truncate font-medium text-gray-700">{key}</span>
              <span className="text-right text-gray-900">{value}</span>
            </div>
          )
        )}
      </div>
      {Object.keys(rcsDashboardData?.failedDetails || {}).length === 0 && (
        <div className="text-center text-gray-400 py-2">No failed items</div>
      )}
    </div>
  );

  const UserformatFilterDisplay = (filterData) => {
    const activeFilters = [];

    if (filterData.companyName) {
      activeFilters.push(`Company Name: ${filterData.companyName}`);
    }

    if (filterData.campaignId) {
      activeFilters.push(`Campaign ID: ${filterData.campaignId}`);
    }
    if (filterData.fromDate) {
      activeFilters.push(`Start Date: ${filterData.fromDate || fromDateError}`);
    }
    if (filterData.toDate) {
      activeFilters.push(`End Date: ${filterData.toDate}`);
    }

    if (activeFilters.length === 0) {
      return null;
    }

    const [first, ...rest] = activeFilters;
    return rest.length > 0 ? `${first} +${rest.length} more` : first;
  };

  const openModalFilter = () => {
    setIsModalOpenFilter(true);
  };

  const closeModalFilter = () => {
    setIsModalOpenFilter(false);
  };

  const toggleFilters = () => {
    setShowFilters((prevState) => !prevState);
  };

  const handleReset = () => {
    setFilterData(defaultFilterData);
  };

  const handleToggle = () => {
    handleReset();

    const payload = {
      ...defaultFilterData,
      fromDate: formatDateForBackend(defaultFilterData.fromDate),
      toDate: formatDateForBackend(defaultFilterData.toDate),
    };

    callApi(payload);
    setShowFilters(false);
    // setCustomerData([]);
  };

  return (
    <div className="bg-[#F9FAFB] rounded-xl p-6 mb-6 shadow">
      {/* Date Filters */}
      <div className="mb-6 flex gap-4 justify-end">
        {/* {!filterData && (
          <div className="mb-6 flex gap-4">
            <div className="flex flex-col">
              <div className="flex items-center flex-row gap-2">
                <label className="text-sm font-medium text-gray-700">
                  From Date:
                </label>
                <input
                  type="date"
                  value={fromDate}
                  max={getTodayDate()}
                  onChange={handleFromDateChange}
                  className="mt-1 rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  disabled
                />
              </div>
              {fromDateError && (
                <span className="error-text">{fromDateError}</span>
              )}
            </div>

            <div className="flex flex-col">
              <div className="flex items-center flex-row gap-2">
                <label className="text-sm font-medium text-gray-700">
                  To Date:
                </label>
                <input
                  type="date"
                  value={toDate}
                  max={getTodayDate()}
                  onChange={handleToDateChange}
                  className="mt-1 rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  disabled
                />
              </div>
              {toDateError && <span className="error-text">{toDateError}</span>}
            </div>
          </div>
        )} */}

        <div className="filter-container">
          {showFilters && (
            <motion.div
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="search-btn" onClick={openModalFilter}>
                Search/Filter
              </div>
            </motion.div>
          )}

          {showFilters && (
            <motion.div
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="green-btn" onClick={handleToggle}>
                <CachedIcon />
              </div>
            </motion.div>
          )}

          <div
            className="filter-toggle-box"
            title={loading ? "Loading..." : "Toggle Filters"}
          >
            <img
              src={FilterImg}
              alt="funnel"
              className="funnel-icon"
              onClick={!loading ? toggleFilters : undefined}
            />
            <div
              className={`filter-text ${
                filterData && Object.keys(filterData).length > 0 ? "pr-1" : ""
              }`}
            >
              {filterData && UserformatFilterDisplay(filterData)}
            </div>
          </div>
        </div>
      </div>

      {/* Top metrics row */}
      <div className="grid grid-cols-8 gap-2 text-center text-sm font-medium text-gray-700 bg-[#efefef] p-2 border mb-3">
        <div className="border-r border-[#D2D2E2] cursor-pointer">
          <p className="font-bold text-gray-900 text-xl">100%</p>
          <p className="text-gray-900">
            {formatNumberWithCommas(rcsDashboardData?.totalCount)}
          </p>
          <p className="text-[#b0b0b4]">UPLOADED</p>
        </div>
        <div className="border-r border-[#D2D2E2]">
          <p className="font-bold text-gray-900 text-xl">
            {getPercentage(
              rcsDashboardData?.nonCompatibleCount,
              rcsDashboardData?.totalCount
            )}
          </p>
          <p className="text-gray-900">
            {formatNumberWithCommas(rcsDashboardData?.nonCompatibleCount)}
          </p>
          <p className="text-[#b0b0b4]">NON COMPATIBLE</p>
        </div>
        <div className="border-r border-[#D2D2E2]">
          <p className="font-bold text-gray-900 text-xl">
            {getPercentage(
              rcsDashboardData?.sentCount,
              rcsDashboardData?.totalCount
            )}
          </p>
          <p className="text-gray-900">
            {formatNumberWithCommas(rcsDashboardData?.sentCount)}
          </p>
          <p className="text-[#b0b0b4]">SENT</p>
        </div>

        <Tooltip
          title={failedTooltipContent}
          arrow
          placement="bottom"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 8],
                  },
                },
              ],
            },
            tooltip: {
              sx: {
                bgcolor: "white",
                color: "black",
                border: "1px solid #ddd",
                boxShadow: 2,
                p: 1.5,
                borderRadius: "6px",
                fontSize: "14px",
                maxWidth: "400px",
              },
            },
          }}
        >
          <div className="border-r border-[#D2D2E2] cursor-pointer">
            <p className="font-bold text-gray-900 text-xl">
              {getPercentage(
                rcsDashboardData?.failedCount,
                rcsDashboardData?.totalCount
              )}
            </p>
            <p className="text-gray-900">
              {formatNumberWithCommas(rcsDashboardData?.failedCount)}
            </p>
            <p className="text-[#b0b0b4]">FAILURE/DECLINED</p>
          </div>
        </Tooltip>

          <div className="border-r border-[#D2D2E2] cursor-pointer">
            <p className="font-bold text-gray-900 text-xl">
              {getPercentage(
                rcsDashboardData?.pendingCount,
                rcsDashboardData?.totalCount
              )}
            </p>
            <p className="text-gray-900">
              {formatNumberWithCommas(rcsDashboardData?.pendingCount)}
            </p>
            <p className="text-[#b0b0b4]">PENDING AT OEM</p>
          </div>

        <Tooltip
          title={tooltipContent}
          arrow
          placement="top"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 8],
                  },
                },
              ],
            },
            tooltip: {
              sx: {
                bgcolor: "white",
                color: "black",
                border: "1px solid #ddd",
                boxShadow: 2,
                p: 1.5,
                borderRadius: "6px",
                fontSize: "14px",
                maxWidth: "300px",
              },
            },
          }}
        >
          <div className="border-r border-[#D2D2E2] cursor-pointer">
            <p className="font-bold text-gray-900 text-xl">
              {getPercentage(
                rcsDashboardData?.deliveredCount,
                rcsDashboardData?.totalCount
              )}
            </p>
            <p className="text-gray-900">
              {formatNumberWithCommas(rcsDashboardData?.deliveredCount)}
            </p>
            <p className="text-[#b0b0b4]">DELIVERED</p>
          </div>
        </Tooltip>

        <Tooltip
          title={readTooltipContent}
          arrow
          placement="top"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 8],
                  },
                },
              ],
            },
            tooltip: {
              sx: {
                bgcolor: "white",
                color: "black",
                border: "1px solid #ddd",
                boxShadow: 2,
                p: 1.5,
                borderRadius: "6px",
                fontSize: "14px",
                maxWidth: "300px",
              },
            },
          }}
        >
          <div className="border-r border-[#D2D2E2] cursor-pointer">
            <p className="font-bold text-gray-900 text-xl">
              {getPercentage(
                rcsDashboardData?.readCount,
                rcsDashboardData?.totalCount
              )}
            </p>
            <p className="text-gray-900">
              {formatNumberWithCommas(rcsDashboardData?.readCount)}
            </p>
            <p className="text-[#b0b0b4]">READ</p>
          </div>
        </Tooltip>
        <div>
          <p className="font-bold text-gray-900 text-xl">
            {getPercentage(
              rcsDashboardData?.clickCount,
              rcsDashboardData?.totalCount
            )}
          </p>
          <p className="text-gray-900">
            {formatNumberWithCommas(rcsDashboardData?.clickCount)}
          </p>
          <p className="text-[#b0b0b4]">CLICK</p>
        </div>
      </div>

      {/* Progress bars row */}
      <div className="grid [grid-template-columns:auto_auto] gap-4 rounded-lg border bg-[#F1FFFF] p-4">
        {/* Open Rate */}
        <div className="flex flex-col items-center border-r border-[#D2D2E2] pr-8">
          <SemiCircleChart value={rcsDashboardData?.openRate} color="#FFC107" />
          <p className="mt-2 text-xl font-bold text-gray-900">Open Rate</p>
          <p className="mt-2 text-lg font-medium text-[#b0b0b4]">
            Open Rate = (Read / Delivered) * 100
          </p>
        </div>

        {/* Click Rate */}
        <div className="flex flex-col items-center pr-8">
          <SemiCircleChart
            value={rcsDashboardData?.clickRate}
            color="#00b6ce"
          />
          <p className="mt-2 text-xl font-bold text-gray-900">Click Rate</p>
        </div>

        {isModalOpenFilter ? (
          <RcsDashboardFilterModal
            show={isModalOpenFilter}
            handleClose={closeModalFilter}
            setFilterData={setFilterData}
            filterData={filterData}
            handleReset={handleReset}
            setHistoryData={setRCSHistoryData}
            setPageDetails={setPageDetails}
            setNoData={setNoData}
            pageSize={10}
            currentPage={currentPage}
            callApi={callApi}
          />
        ) : null}
      </div>
    </div>
  );
};

export default CampaignAnalytics;
